//utils/assetUtils.js
import { AssetModel } from "../models/AssetModel";

// ─────────────────────────────────────────────────────────────────────────────
// SAFE TIMESTAMP NORMALIZATION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert any of: Date, Firestore Timestamp, { seconds, nanoseconds },
 * ISO string, epoch number → Date | null.
 * Pure function — safe to use anywhere.
 */
export const safeTimestampToDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === "object" && "seconds" in val) {
    return new Date(
      val.seconds * 1000 + Math.round((val.nanoseconds || 0) / 1_000_000)
    );
  }
  const parsed = new Date(val);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// ─────────────────────────────────────────────────────────────────────────────
// MODEL ←→ UI ADAPTER
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wrap a raw DB row in an AssetModel, then expose a UI-friendly view.
 *
 * Design decision: the model stores RAW values. This adapter adds
 * `<field>Date` companions (Date objects) for UI code that needs them.
 * It never overwrites the raw field.
 */
export const normalizeAsset = (data) => {
  if (!data) return null;

  const instance = new AssetModel(data, { isCreation: false });
  const meta = instance.metadata || {};

  // Fields that benefit from a Date companion for display/filtering
  const dateFields = [
    "warrantyExpiry",
    "disposalDate",
    "assignedOn",
    "purchaseDate",
    "maintenanceStartDate",
    "leaseStartDate",
    "leaseEndDate",
    "rentStartDate",
    "expectedReturnDate",
    "expiryDate",
    "returnDate",
  ];

  const dateView = {};
  for (const f of dateFields) {
    dateView[`${f}Date`] = safeTimestampToDate(instance[f]);
  }

  return {
    ...instance,
    ...dateView,

    // Flattened metadata for convenience
    createdAt: safeTimestampToDate(meta.createdAt),
    updatedAt: safeTimestampToDate(meta.modifiedAt),
    createdBy: meta.createdBy || null,
    modifiedBy: meta.modifiedBy || null,
    deletedFlag: meta.deletedFlag || false,
  };
};
export const stripUndefined = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (value !== null && typeof value === "object") {
    if (
      value instanceof Date ||
      typeof value.toDate === "function" ||
      typeof value.seconds === "number"
    ) {
      return value;
    }
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out;
  }
  return value;
};
// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & ENUMS
// ─────────────────────────────────────────────────────────────────────────────

export const ASSET_COLLECTION = "assets";
export const CONSUMPTION_COLLECTION = "consumptions";
export const PREFERENCES_COLLECTION = "preferences";

export const ASSET_ID_PREF_NAME = "assets_id";
export const ASSET_ID_PREF_TYPE = "Asset Id";

export const ENTITY_PREFIXES = Object.freeze({
  USER: "USR", BRAND: "BRD", CATEGORY: "CAT", SUBCATEGORY: "SUB",
  VENDOR: "VEN", DEPARTMENT: "DEP", LOCATION: "LOC", TICKET: "TKT",
  SUPPORT: "SUP", ASSIGNMENT: "ASG", REQUEST: "REQ", INVOICE: "INV",
  PURCHASE: "PUR", MAINTENANCE: "MNT", ALERT: "ALT", NOTIFICATION: "NOT",
});

export const ASSET_TYPES = Object.freeze({
  PRIMARY: "Primary Asset",
  PARENT: "Parent Asset", 
  SUB: "Sub Asset",
  COMPONENT: "Component",
  ACCESSORY: "Accessory",
  CONSUMABLE: "Consumable",
  SPARE_PART: "Spare Part",
  CUSTOMER: "Customer",
});

export const OWNERSHIP_TYPES = Object.freeze({
  OWNED: "Owned",
  LEASED: "Leased",
  RENTED: "Rented",
});

export const ASSET_STATUS = Object.freeze({
  AVAILABLE: "Available",
  ASSIGNED: "Assigned",
  DISPOSED: "Disposed",
  RETURNED: "Returned",
  IN_MAINTENANCE: "In Maintenance",
});

export const ASSET_STATUSES = Object.freeze({
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  DISPOSED: "Disposed",
  RETURNED: "Returned",
  IN_REPAIR: "In Repair",
});

export const ASSIGNMENT_TYPES = Object.freeze({
  INITIAL: "initial",
  ASSIGNED: "assigned",
  REASSIGNED: "reassigned",
  RELEASED: "released",
});

export const DEPRECIATION_METHODS = Object.freeze({
  SLM: "slm",
  WDV: "wdv",
  NONE: "none",
});
export const SERIALIZATION_TYPES = Object.freeze({
  SERIALIZED: "serialized",
  NON_SERIALIZED: "non-serialized",
});

export const USER_ROLES = Object.freeze({
  SUPERADMIN: "superadmin",
  ADMIN: "admin",
  LOCATION_ADMIN: "locationAdmin",
});

export const LOCATION_STATUSES = Object.freeze({
  ACTIVE: "active",
});

export const PREFERENCE_TYPES = Object.freeze({
  ASSET_TYPE: "Asset Type",
  OWNERSHIP_TYPE: "Ownership Type",
});

export const AUDIT_ACTION_TYPES = Object.freeze({
  LOCATION_CHANGE: "location_change",
});

export const AUDIT_ENTITY_TYPES = Object.freeze({
  ASSET: "Asset",
});

export const STOCK_MANAGED_ASSET_TYPES = Object.freeze([
  ASSET_TYPES.CONSUMABLE,
  ASSET_TYPES.SPARE_PART,
]);

export const ASSET_TYPES_NO_DISPOSAL = STOCK_MANAGED_ASSET_TYPES;
export const ASSET_TYPES_CONSUMABLE = STOCK_MANAGED_ASSET_TYPES;
export const ASSET_TYPE_OPTIONS = Object.values(ASSET_TYPES);
export const OWNERSHIP_TYPE_OPTIONS = Object.values(OWNERSHIP_TYPES);
export const ASSET_TYPES_SHOW_PARENT = [
  ASSET_TYPES.SUB, ASSET_TYPES.COMPONENT, ASSET_TYPES.ACCESSORY,
];
export const OWNERSHIP_TYPES_RETURN_ONLY = [
  OWNERSHIP_TYPES.LEASED, OWNERSHIP_TYPES.RENTED,
];
export const MOVEABLE_ASSET_TYPES = [
  ASSET_TYPES.SUB, ASSET_TYPES.COMPONENT, ASSET_TYPES.ACCESSORY,
];
export const MOVABLE_CHILD_TYPES = MOVEABLE_ASSET_TYPES;
export const EXCLUDED_FROM_MOVE_TYPES = [
  ASSET_TYPES.CONSUMABLE, ASSET_TYPES.SPARE_PART,
];

export const DISPOSAL_REASONS = [
  "End of Life - Normal depreciation",
  "Damaged beyond repair",
  "Obsolete technology",
  "Lost or stolen",
  "Sold to third party",
  "Donated",
  "Recycled",
  "Other",
];

export const CSV_EXPORT_HEADERS = Object.freeze([
  { label: "S.No", key: "S.No" },
  { label: "From Location", key: "From Location" },
  { label: "To Location", key: "To Location" },
  { label: "Changed By", key: "Changed By" },
  { label: "Change Date", key: "Change Date" },
  { label: "Reason", key: "Reason" },
  { label: "Quantity Moved", key: "Quantity Moved" },
]);

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURATIONS
// ─────────────────────────────────────────────────────────────────────────────

export const ASSET_TYPE_CONFIG = Object.freeze({
  [ASSET_TYPES.PRIMARY]: {
    requiresParent: false, canBeParent: true,
    hasProcurementInheritance: false, hasLocationInheritance: false,
    serialPolicy: "sub-category", tracksQuantity: true,
    hasReorderLevel: false, hasExpiryDate: false,
  },
  [ASSET_TYPES.PARENT]: {
    requiresParent: false, canBeParent: true, 
    hasProcurementInheritance: false, hasLocationInheritance: false,
    serialPolicy: "sub-category", tracksQuantity: true,
    hasReorderLevel: false, hasExpiryDate: false,
  },
  [ASSET_TYPES.SUB]: {
    requiresParent: true, canBeParent: false,
    hasProcurementInheritance: true, hasLocationInheritance: true,
    serialPolicy: "sub-category", tracksQuantity: false,
    hasReorderLevel: false, hasExpiryDate: false,
  },
  [ASSET_TYPES.COMPONENT]: {
    requiresParent: true, canBeParent: false,
    hasProcurementInheritance: true, hasLocationInheritance: true,
    serialPolicy: "sub-category", tracksQuantity: false,
    hasReorderLevel: false, hasExpiryDate: false,
  },
  [ASSET_TYPES.ACCESSORY]: {
    requiresParent: true, canBeParent: false,
    hasProcurementInheritance: false, hasLocationInheritance: true,
    serialPolicy: "sub-category", tracksQuantity: true,
    hasReorderLevel: false, hasExpiryDate: false,
  },
  [ASSET_TYPES.CONSUMABLE]: {
    requiresParent: false, canBeParent: false,
    hasProcurementInheritance: false, hasLocationInheritance: false,
    serialPolicy: "disabled", tracksQuantity: true,
    hasReorderLevel: true, hasExpiryDate: true,
  },
  [ASSET_TYPES.SPARE_PART]: {
    requiresParent: false, canBeParent: false,
    hasProcurementInheritance: false, hasLocationInheritance: false,
    serialPolicy: "sub-category", tracksQuantity: true,
    hasReorderLevel: true, hasExpiryDate: false,
  },
  [ASSET_TYPES.CUSTOMER]: {
    requiresParent: false, canBeParent: false,
    hasProcurementInheritance: false, hasLocationInheritance: false,
    serialPolicy: "sub-category", tracksQuantity: true,
    hasReorderLevel: false, hasExpiryDate: false,
    showsSalesWarranty: true, 
  },
 
});

export const DEFAULT_ASSET_TYPE_CONFIG = Object.freeze({
  requiresParent: false, canBeParent: false,
  hasProcurementInheritance: false, hasLocationInheritance: false,
  serialPolicy: "sub-category", tracksQuantity: false,
  hasReorderLevel: false, hasExpiryDate: false,
});

export const OWNERSHIP_TYPE_CONFIG = Object.freeze({
  [OWNERSHIP_TYPES.OWNED]: {
    showsFinancialSection: true, showsLeaseFields: false,
    showsRentalFields: false, requiresBillingFrequency: false,
    showsDepreciation: true,
  },
  [OWNERSHIP_TYPES.LEASED]: {
    showsFinancialSection: false, showsLeaseFields: true,
    showsRentalFields: false, requiresBillingFrequency: true,
    showsDepreciation: false,
  },
  [OWNERSHIP_TYPES.RENTED]: {
    showsFinancialSection: false, showsLeaseFields: false,
    showsRentalFields: true, requiresBillingFrequency: true,
    showsDepreciation: false,
  },
});

export const DEFAULT_OWNERSHIP_TYPE_CONFIG = Object.freeze({
  showsFinancialSection: false, showsLeaseFields: false,
  showsRentalFields: false, requiresBillingFrequency: false,
  showsDepreciation: false,
});

export const ASSIGNMENT_TYPE_CONFIG = Object.freeze({
  [ASSIGNMENT_TYPES.INITIAL]: {
    bg: "#f0fdf4", border: "#86efac", text: "#166534",
    label: "Initial Assignment",
  },
  [ASSIGNMENT_TYPES.ASSIGNED]: {
    bg: "#eff6ff", border: "#93c5fd", text: "#1e40af",
    label: "Assigned",
  },
  [ASSIGNMENT_TYPES.REASSIGNED]: {
    bg: "#faf5ff", border: "#c4b5fd", text: "#6b21a8",
    label: "Re-assigned",
  },
  [ASSIGNMENT_TYPES.RELEASED]: {
    bg: "#fff7ed", border: "#fdba74", text: "#9a3412",
    label: "Released",
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT STATE SHAPES
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_ASSET_IDENTITY = Object.freeze({
  assetId: "", assetName: "", assetDescription: "", company: "",
  companyId: "", assetType: "", ownershipType: "", categoryId: "",
  categoryName: "", categoryCode: "", subCategoryId: "",
  subCategoryName: "", brandId: "", brandName: "",
});

export const DEFAULT_ASSET_PARENT = Object.freeze({
  parentAssetId: "", parentAssetName: "", purchasedSeparately: false,
});

export const DEFAULT_ASSET_TRACKING = Object.freeze({
  serializationType: "serialized", manufacturerSerialNumber: "",
  userTrackingMode: "serialized", quantity: 1, unitOfMeasurement: "units",
  reorderLevel: "", expiryDate: "",
});

export const DEFAULT_ASSET_ASSIGNMENT = Object.freeze({
  status: ASSET_STATUS.AVAILABLE, assignedTo: "", assignedToName: "",
  assignedDepartmentId: "", assignedDepartmentName: "",
});

export const DEFAULT_ASSET_LOCATION = Object.freeze({ locationCode: "" });

export const DEFAULT_ASSET_PROCUREMENT = Object.freeze({
  purchaseDate: "", purchaseValue: "", vendorId: "", vendorName: "",
  warrantyExpiry: "", invoiceNumber: "", invoiceFileUrl: "",
  invoiceFileName: "", invoiceFile: null,
});

export const DEFAULT_ASSET_LEASE = Object.freeze({
  leaseStartDate: "", leaseEndDate: "", leaseAmount: "",
  leaseVendorId: "", leaseVendorName: "", leaseAgreementNumber: "",
  leaseBillingFrequency: "",
});

export const DEFAULT_ASSET_RENTAL = Object.freeze({
  rentStartDate: "", expectedReturnDate: "", rentAmount: "",
  rentVendorId: "", rentVendorName: "", rentAgreementNumber: "",
  rentBillingFrequency: "",
});

export const DEFAULT_ASSET_DEPRECIATION = Object.freeze({
  depreciationMethod: "", usefulLife: "", depreciationRate: "",
  residualValue: "", openingBookValue: "", isDepreciationAuto: true,
  isOpeningBookValueManual: false, depreciationSchedule: "",
});

export const DEFAULT_ASSET_MAINTENANCE = Object.freeze({
  maintenanceFrequency: "", maintenanceStartDate: "",
});

export const DEFAULT_ASSET_META = Object.freeze({ qrCodeValue: "" });

export const DEFAULT_ASSET = Object.freeze({
  ...DEFAULT_ASSET_IDENTITY, ...DEFAULT_ASSET_PARENT,
  ...DEFAULT_ASSET_TRACKING, ...DEFAULT_ASSET_ASSIGNMENT,
  ...DEFAULT_ASSET_LOCATION, ...DEFAULT_ASSET_PROCUREMENT,
  ...DEFAULT_ASSET_LEASE, ...DEFAULT_ASSET_RENTAL,
  ...DEFAULT_ASSET_DEPRECIATION, ...DEFAULT_ASSET_MAINTENANCE,
  ...DEFAULT_ASSET_META,
});

// ─────────────────────────────────────────────────────────────────────────────
// DOMAIN PREDICATES
// ─────────────────────────────────────────────────────────────────────────────

export const isAssetAssigned = (a) =>
  a?.status === ASSET_STATUS.ASSIGNED && Boolean(a?.assignedTo);

export const isAssetDeleted = (d) =>
  d?.deleted === true || d?.isDeleted === true;

export const isPrimaryAsset = (a) =>
  !a?.assetType || a.assetType === ASSET_TYPES.PRIMARY;

export const isConsumableAsset = (a) =>
  a?.assetType === ASSET_TYPES.CONSUMABLE ||
  a?.assetType === ASSET_TYPES.SPARE_PART;

export const isDisposalBlocked = (t) => STOCK_MANAGED_ASSET_TYPES.includes(t);
export const canBeDisposed = (t) => !STOCK_MANAGED_ASSET_TYPES.includes(t);
export const canRecordConsumption = (t) => STOCK_MANAGED_ASSET_TYPES.includes(t);
export const isReturnOnlyAsset = (o) => OWNERSHIP_TYPES_RETURN_ONLY.includes(o);

export const isSerializedAsset = (a) => {
  if (a?.serializationType === "serialized") return true;
  if (a?.serializationType === "non-serialized") return false;
  if (a?.unitOfMeasurement) return false;
  return (a?.quantity ?? 1) === 1;
};

export const isAssetDisposed = (a) =>
  a?.status === ASSET_STATUSES.DISPOSED || a?.isDisposed === true;

export const isNonSerializedAsset = (a) =>
  a?.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED;

export const isSparePartAsset = (a) => a?.assetType === ASSET_TYPES.SPARE_PART;

export const isMovableChildAsset = (a) => MOVABLE_CHILD_TYPES.includes(a?.assetType);

export const isExcludedFromMove = (a) => EXCLUDED_FROM_MOVE_TYPES.includes(a?.assetType);

export const isLocationActive = (l) =>
  !l?.status || l.status === LOCATION_STATUSES.ACTIVE;

export const isWarrantyExtensionBlocked = (a) =>
  a?.status === ASSET_STATUS.DISPOSED || a?.isDisposed === true;

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG RESOLVERS
// ─────────────────────────────────────────────────────────────────────────────

export const getAssetTypeConfig = (t) =>
  ASSET_TYPE_CONFIG[t] ?? DEFAULT_ASSET_TYPE_CONFIG;

export const getOwnershipTypeConfig = (o) =>
  OWNERSHIP_TYPE_CONFIG[o] ?? DEFAULT_OWNERSHIP_TYPE_CONFIG;

export const getAssignmentTypeConfig = (t) =>
  ASSIGNMENT_TYPE_CONFIG[t] ?? ASSIGNMENT_TYPE_CONFIG[ASSIGNMENT_TYPES.ASSIGNED];

export const requiresParentAsset = (t) => getAssetTypeConfig(t).requiresParent;
export const canBeParent = (t) => getAssetTypeConfig(t).canBeParent;
export const hasProcurementInheritance = (t) => getAssetTypeConfig(t).hasProcurementInheritance;
export const hasLocationInheritance = (t) => getAssetTypeConfig(t).hasLocationInheritance;

export const resolveEffectiveSerialPolicy = (typeName, subCat) => {
  const policy = getAssetTypeConfig(typeName).serialPolicy;
  if (policy === "disabled") return "disabled";
  if (policy === "required") return "required";
  const m =
    subCat?.assetTrackingMethod ||
    subCat?.trackingMethod ||
    subCat?.serializationType ||
    "";
  if (m === "serialized") return "required";
  if (m === "non-serialized") return "disabled";
  return "optional";
};

export const getDepreciationLabel = (m) => {
  if (!m) return "—";
  return { slm: "SLM", wdv: "WDV", none: "None" }[m] ?? m;
};

// Legacy alias map — keeps older sub-category records working until migrated
const DEPRECIATION_ALIASES = {
  "straight-line":       "slm",
  "straight_line":       "slm",
  "straight line":       "slm",
  "SLM":                 "slm",
  "slm":                 "slm",
  "written-down-value":  "wdv",
  "written_down_value":  "wdv",
  "written down value":  "wdv",
  "WDV":                 "wdv",
  "wdv":                 "wdv",
  "None":                "none",
  "NONE":                "none",
  "none":                "none",
};

export const normalizeDepreciationMethod = (raw) => {
  if (!raw) return "";
  const key = String(raw).trim();
  if (["slm", "wdv", "none"].includes(key)) return key;
  return DEPRECIATION_ALIASES[key] ?? DEPRECIATION_ALIASES[key.toLowerCase()] ?? "";
};

export const getSubCategoryDepreciationConfig = (sc) => {
  if (!sc) return { depreciationMethod: "", usefulLife: "", depreciationRate: "" };
  const rawMethod = sc.depreciation || sc.depreciationMethod || "";
  const m = normalizeDepreciationMethod(rawMethod);
  return {
    depreciationMethod: m,
    usefulLife: m === DEPRECIATION_METHODS.SLM && sc.usefulLife != null ? String(sc.usefulLife) : "",
    depreciationRate: m === DEPRECIATION_METHODS.WDV && sc.depreciationRate != null ? String(sc.depreciationRate) : "",
  };
};

export const getAssetDepreciation = (asset, subCategoryMap) => {
  const custom =
    (asset.depreciationMethod && asset.depreciationMethod !== "") ||
    (asset.depreciation && asset.depreciation !== "") ||
    (asset.usefulLife != null && asset.usefulLife !== "") ||
    (asset.depreciationRate != null && asset.depreciationRate !== "") ||
    (asset.residualValue != null && asset.residualValue !== "");
  if (custom) {
    return {
     method: normalizeDepreciationMethod(
  asset.depreciationMethod || asset.depreciation || "slm"
),
      usefulLife: asset.usefulLife ?? null,
      depreciationRate: asset.depreciationRate ?? null,
      residualValue: asset.residualValue ?? 0,
      source: "custom",
      hasDepreciation: true,
    };
  }
  if (asset.subCategoryId) {
    const sc = subCategoryMap?.get(asset.subCategoryId);
    if (sc) {
      const m = sc.depreciation || sc.depreciationMethod;
      if (m) {
        return {
          method: m,
          usefulLife: sc.usefulLife ?? null,
          depreciationRate: sc.depreciationRate ?? null,
          residualValue: sc.residualValue ?? 0,
          source: "inherited",
          subCategoryName: sc.subCategoryName,
          hasDepreciation: true,
        };
      }
    }
  }
  return {
    method: null, usefulLife: null, depreciationRate: null,
    residualValue: 0, source: "none", hasDepreciation: false,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// FORMATTING & EXTRACTION
// ─────────────────────────────────────────────────────────────────────────────

export const getTotalDisposed = (h = []) =>
  h.reduce((s, e) => s + (Number(e.disposedQuantity) || 0), 0);

export const resolveFinalReason = (r, c) =>
  r === "Other" ? (c?.trim() ?? "") : r;

export const getSortedAssignmentHistory = (a) =>
  Array.isArray(a?.assignmentHistory) ? a.assignmentHistory.slice().reverse() : [];

export const getAssignmentSuccessMessage = (t, n = "") => {
  if (t === ASSIGNMENT_TYPES.REASSIGNED) return `Asset re-assigned to ${n} successfully.`;
  if (t === ASSIGNMENT_TYPES.RELEASED) return "Asset released successfully.";
  return `Asset assigned to ${n} successfully.`;
};

export const getWarrantyMinDate = (asset) => {
  const today = new Date().toISOString().split("T")[0];
  const raw = asset?.warrantyExpiry;
  if (!raw) return today;
  const ce = safeTimestampToDate(raw);
  if (!ce) return today;
  if (ce > new Date()) {
    const n = new Date(ce);
    n.setDate(n.getDate() + 1);
    return n.toISOString().split("T")[0];
  }
  return today;
};

export const sortConsumptionsByDate = (r) =>
  [...r].sort(
    (a, b) =>
      new Date(b.consumptionDate || b.createdDate || 0) -
      new Date(a.consumptionDate || a.createdDate || 0)
  );

export const padSequenceNumber = (num = 1, padLength = 5) => {
  const parsed = parseInt(num, 10);
  const validNum = isNaN(parsed) || parsed < 1 ? 1 : parsed;
  return String(validNum).padStart(padLength, "0");
};

export const removeUndefined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

export const formatLocationName = (l) =>
  l ? `${l.locationName || ""}, ${l.city || ""}, ${l.state || ""}` : "";

export const createPreferenceOption = (raw = {}) => ({
  value: raw.preferenceValue ?? "",
  label: raw.preferenceName ?? "",
});

export const createExportRow = (entry, index) => ({
  "S.No": index + 1,
  "From Location": entry.from ?? "—",
  "To Location": entry.to ?? "—",
  "Changed By": entry.changedBy ?? "—",
  "Change Date":
    entry.changedAt instanceof Date
      ? entry.changedAt.toLocaleString()
      : (entry.changedAt?.toDate?.()?.toLocaleString?.() ?? "N/A"),
  Reason: entry.reason ?? "—",
  "Quantity Moved": entry.quantityMoved ?? "N/A",
});

export const buildAuditDescription = ({
  assetName, from, to, reason,
  isNonSerialized, quantityToMove, hasChildren, childCount,
}) => {
  const q = isNonSerialized ? ` (${quantityToMove} units)` : "";
  const c = hasChildren && childCount > 0 ? ` + ${childCount} child asset(s)` : "";
  return `Asset "${assetName}" moved from "${from}" to "${to}"${q}${c} — Reason: ${reason}`;
};

export const buildSuccessMessage = ({
  hasChildren, childCount, isNonSerialized,
  quantityToMove, unitOfMeasurement,
}) => {
  const c = hasChildren && childCount > 0 ? ` and ${childCount} child asset(s)` : "";
  const q = isNonSerialized ? ` (${quantityToMove} ${unitOfMeasurement || "units"} moved)` : "";
  return `Asset location updated${c}!${q}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// FORM VALIDATORS (return field→error maps for UI forms)
// ─────────────────────────────────────────────────────────────────────────────

export const validateDisposalForm = ({
  disposalDate, disposalReason, customReason,
  disposeQuantity, isSerialized, availableQty,
}) => {
  const e = {};
  if (!disposalDate) {
    e.disposalDate = "Please select a disposal date";
  } else {
    const [y, m, d] = disposalDate.split("-").map(Number);
    const s = new Date(y, m - 1, d);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    if (s > t) e.disposalDate = "Disposal date cannot be in the future";
  }
  if (!disposalReason) e.disposalReason = "Please select a reason for disposal";
  if (disposalReason === "Other" && !customReason?.trim()) {
    e.customReason = "Please specify the reason for disposal";
  }
  if (!isSerialized) {
    const q = Number(disposeQuantity);
    if (!disposeQuantity || isNaN(q) || q < 1) {
      e.disposeQuantity = "Please enter a valid quantity to dispose";
    } else if (q > availableQty) {
      e.disposeQuantity = `Cannot dispose more than available quantity (${availableQty})`;
    }
  }
  return e;
};

export const validateAssignmentForm = ({ assignedTo, assignedDepartmentId }) => {
  const e = {};
  if (!assignedTo) e.assignedTo = "Please select a user to assign to.";
  if (!assignedDepartmentId) e.assignedDepartmentId = "Please select a department.";
  return e;
};

export const validateWarrantyExtensionForm = ({
  newExpiryDate, extensionReason,
  warrantyReferenceNumber, warrantyCost, asset,
}) => {
  const e = {};
  if (!newExpiryDate) {
    e.newExpiryDate = "Please select a new warranty expiry date";
  } else if (asset?.warrantyExpiry) {
    const ne = new Date(newExpiryDate);
    const ce = safeTimestampToDate(asset.warrantyExpiry);
    if (ce && ne <= ce) {
      e.newExpiryDate = "New expiry date must be after the current expiry date";
    }
  }
  if (!extensionReason?.trim()) e.extensionReason = "Please provide a reason for warranty extension";
  if (!warrantyReferenceNumber?.trim()) e.warrantyReferenceNumber = "Please provide a warranty reference number";
  const cs = warrantyCost?.toString().trim();
  if (!cs) {
    e.warrantyCost = "Please provide the warranty cost";
  } else if (isNaN(parseFloat(cs)) || parseFloat(cs) < 0) {
    e.warrantyCost = "Please enter a valid positive amount";
  }
  return e;
};

export const validateConsumption = (d) => {
  const e = [];
  if (!d?.assetId) e.push("Asset ID is required.");
  if (!d?.quantity || Number(d.quantity) <= 0) e.push("Quantity must be greater than zero.");
  if (!d?.consumptionDate) e.push("Consumption date is required.");
  return e;
};

export const validateReturnForm = ({ returnDate, remarks }) => {
  const e = {};
  if (!returnDate) e.returnDate = "Return date is required.";
  if (returnDate) {
    const [y, m, d] = String(returnDate).split("-").map(Number);
    const picked = new Date(y, (m || 1) - 1, d || 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (picked > today) e.returnDate = "Return date cannot be in the future.";
  }
  if (remarks && remarks.length > 500) {
    e.remarks = "Remarks cannot exceed 500 characters.";
  }
  return e;
};

// ─────────────────────────────────────────────────────────────────────────────
// BUILDERS (produce partial payloads for specific workflows)
// ─────────────────────────────────────────────────────────────────────────────

export const buildDisposalEntry = ({
  disposalDate, disposalReason, disposedQuantity, disposedBy,
}) => ({
  disposalDate: disposalDate instanceof Date ? disposalDate.toISOString() : disposalDate,
  disposalReason,
  disposedQuantity: Number(disposedQuantity) || 0,
  disposedBy,
  disposedAt: new Date().toISOString(),
});

export const buildAssignmentEntry = ({
  type, assignedTo, assignedToName,
  assignedDepartmentId, assignedDepartmentName,
  assignedBy, assignedByName, notes = "",
  expectedReturnDate = null,
  previousAssignedTo = null, previousAssignedToName = null,
  releasedFromUser = null, releasedFromUserName = null,
}) => ({
  id: `asg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  type,
  assignedTo: assignedTo ?? null,
  assignedToName: assignedToName ?? null,
  assignedDepartmentId: assignedDepartmentId ?? null,
  assignedDepartmentName: assignedDepartmentName ?? null,
  assignedOn: new Date().toISOString(),
  assignedBy, assignedByName,
  notes: notes.trim(),
  expectedReturnDate: expectedReturnDate || null,
  ...(previousAssignedTo && { previousAssignedTo }),
  ...(previousAssignedToName && { previousAssignedToName }),
  ...(releasedFromUser && { releasedFromUser }),
  ...(releasedFromUserName && { releasedFromUserName }),
});

export const buildAssignPayload = ({
  asset, assignedTo, assignedToName,
  assignedDepartmentId, assignedDepartmentName,
  notes, expectedReturnDate, currentUser,
}) => {
  const now = new Date().toISOString();
  const isRe = Boolean(asset?.assignedTo) && assignedTo !== asset.assignedTo;
  const entryType = isRe ? ASSIGNMENT_TYPES.REASSIGNED : ASSIGNMENT_TYPES.ASSIGNED;
  const entry = buildAssignmentEntry({
    type: entryType, assignedTo, assignedToName,
    assignedDepartmentId, assignedDepartmentName,
    assignedBy: currentUser.uid,
    assignedByName: currentUser.displayName || currentUser.email,
    notes, expectedReturnDate,
    ...(isRe && {
      previousAssignedTo: asset.assignedTo,
      previousAssignedToName: asset.assignedToName,
    }),
  });
  return {
    payload: {
      assignedTo, assignedToName, assignedDepartmentId, assignedDepartmentName,
      status: ASSET_STATUS.ASSIGNED,
      assignedOn: now, updatedAt: now, updatedBy: currentUser.email,
      assignmentHistory: [
        ...(Array.isArray(asset?.assignmentHistory) ? asset.assignmentHistory : []),
        entry,
      ],
    },
    isReassignment: isRe,
    entryType,
  };
};

export const buildReleasePayload = ({ asset, notes, currentUser }) => {
  const now = new Date().toISOString();
  const entry = buildAssignmentEntry({
    type: ASSIGNMENT_TYPES.RELEASED,
    assignedTo: null, assignedToName: null,
    assignedDepartmentId: null, assignedDepartmentName: null,
    assignedBy: currentUser.uid,
    assignedByName: currentUser.displayName || currentUser.email,
    notes: notes || "Asset released / unassigned.",
    releasedFromUser: asset.assignedTo,
    releasedFromUserName: asset.assignedToName,
  });
  return {
    assignedTo: null, assignedToName: null,
    assignedDepartmentId: null, assignedDepartmentName: null,
    status: ASSET_STATUS.AVAILABLE,
    assignedOn: null, updatedAt: now, updatedBy: currentUser.email,
    assignmentHistory: [
      ...(Array.isArray(asset?.assignmentHistory) ? asset.assignmentHistory : []),
      entry,
    ],
  };
};

export const buildWarrantyExtensionEntry = ({
  asset, newExpiry, extensionReason,
  warrantyReferenceNumber, warrantyCost, currentUser,
}) => ({
  id: `wrr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  previousExpiry: asset?.warrantyExpiry ?? null,
  newExpiry: newExpiry instanceof Date ? newExpiry.toISOString() : newExpiry,
  extensionReason:          String(extensionReason ?? '').trim(),
  warrantyReferenceNumber:  String(warrantyReferenceNumber ?? '').trim(),
  warrantyCost:             Number(warrantyCost) || 0,
  updatedBy:                currentUser?.email ?? '',
  updatedAt:                new Date().toISOString(),
});

export const buildWarrantyExtensionPayload = ({
  asset, newExpiry, extensionReason,
  warrantyReferenceNumber, warrantyCost, currentUser,
}) => {
  const now = new Date().toISOString();
  const entry = buildWarrantyExtensionEntry({
    asset, newExpiry, extensionReason,
    warrantyReferenceNumber, warrantyCost, currentUser,
  });
  return {
    warrantyExpiry: newExpiry instanceof Date ? newExpiry.toISOString() : newExpiry,
    warrantyHistory: [
      ...(Array.isArray(asset?.warrantyHistory) ? asset.warrantyHistory : []),
      entry,
    ],
    updatedAt: now,
    updatedBy: currentUser?.email ?? "",
  };
};

export const createLocationHistoryEntry = (raw = {}) => {
  const id = raw.id || `loc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const entry = {
    id,
    from: raw.from ?? "",
    to: raw.to ?? "",
    changedAt: raw.changedAt ? new Date(raw.changedAt) : new Date(),
    changedBy: raw.changedBy ?? "",
    reason: raw.reason ?? "",
    previousLocationCode: raw.previousLocationCode ?? "",
  };
  if (raw.quantityMoved !== null && raw.quantityMoved !== undefined) {
    entry.quantityMoved = Number(raw.quantityMoved) || 0;
  }
  return entry;
};

export const createLocationUpdatePayload = (raw = {}) => {
  const p = {
    locationCode: raw.locationCode ?? "",
    updatedAt: raw.updatedAt ?? new Date().toISOString(),
    updatedBy: raw.updatedBy ?? "",
    locationHistory: Array.isArray(raw.locationHistory) ? raw.locationHistory : [],
  };
  if (raw.lastPartialMoveQuantity !== undefined) {
    p.lastPartialMoveQuantity = Number(raw.lastPartialMoveQuantity) || 0;
  }
  return Object.fromEntries(
    Object.entries(p).filter(([, v]) => v !== undefined)
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RETURN / DISPOSAL HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export const RETURN_ONLY_OWNERSHIP = Object.freeze([
  OWNERSHIP_TYPES.LEASED,
  OWNERSHIP_TYPES.RENTED,
]);

export const isReturnableAsset = (asset) =>
  RETURN_ONLY_OWNERSHIP.includes(asset?.ownershipType);

export const isDisposable = (asset) =>
  !STOCK_MANAGED_ASSET_TYPES.includes(asset?.assetType) &&
  !isReturnableAsset(asset);

export const buildReturnPayload = ({ asset, returnDate, remarks, currentUser }) => {
  const now = new Date().toISOString();
  const entry = {
    id: `ret-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    returnDate: returnDate
      ? (returnDate instanceof Date ? returnDate.toISOString() : returnDate)
      : now,
    remarks: (remarks || "").trim(),
    returnedBy: currentUser?.uid || "",
    returnedByEmail: currentUser?.email || "",
    returnedAt: now,
  };
  return {
    status: ASSET_STATUS.RETURNED,
    returnDate: entry.returnDate,
    returnRemarks: entry.remarks,
    returnHistory: [
      ...(Array.isArray(asset?.returnHistory) ? asset.returnHistory : []),
      entry,
    ],
    updatedAt: now,
    updatedBy: currentUser?.email || "",
  };
};
export const getWarrantyStatus = (asset) => {
  if (!asset || !asset.warrantyExpiry) return { status: "Unknown", color: "default" };
  
  const expiry = safeTimestampToDate(asset.warrantyExpiry);
  if (!expiry) return { status: "Unknown", color: "default" };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (expiry < today) {
    return { status: "Expired", color: "error" };
  }
  
  // Within 30 days
  const diffTime = Math.abs(expiry - today);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 30) {
    return { status: "Expiring Soon", color: "warning" };
  }

  return { status: "Active", color: "success" };
};